import { ApprovalMode } from "./approvalModes";
import { withAuthoringService } from "./boardAuthoringDeps";
import {
  type ClientToolExecutionResult,
  createClientTool,
  toolSuccess,
} from "./ClientTools";
import {
  draftResult,
  EDITOR_OPENING_ID_PROPERTY,
  invalidEditorOpeningIdError,
  isEditorOpeningId,
  noEditorError,
  otherEditorError,
  STAGE_CHANGEABILITY_NOTE,
} from "./panelDraftToolShared";

export interface ReadPanelDraftInput {
  editor_opening_id?: unknown;
}

async function execute(
  input: ReadPanelDraftInput,
): Promise<ClientToolExecutionResult> {
  const editorOpeningId = input.editor_opening_id;
  if (!isEditorOpeningId(editorOpeningId)) {
    return invalidEditorOpeningIdError();
  }
  return withAuthoringService((service) => {
    const result = service.readPanelDraft({ editorOpeningId });
    switch (result.status) {
      case "no-editor": {
        return noEditorError(editorOpeningId, "read");
      }
      case "other-editor": {
        return otherEditorError(editorOpeningId, result.boardId);
      }
      case "draft": {
        return toolSuccess("read_panel_draft", {
          ...draftResult(result.draft),
          note:
            "Nothing in this draft is saved. Change it with " +
            "client_update_panel_draft, passing editor_opening_id and this " +
            `version; the user reviews the change and saves. ${STAGE_CHANGEABILITY_NOTE}`,
        });
      }
    }
  });
}

/**
 * `client_read_panel_draft`: the unsaved draft of the panel editor open in
 * this tab, which `client_read_board` cannot see because it reads only the
 * saved board. Read-only, so it auto-executes by default.
 */
export const tool = createClientTool<ReadPanelDraftInput>({
  name: "client_read_panel_draft",
  description:
    "Reads the unsaved draft in the panel editor open in this tab, which " +
    "client_read_board cannot see, and changes nothing. Call it while " +
    "misc_context.board.editing is present, with its editor_opening_id. " +
    "Returns the draft's panel (null while it has no panel kind); each of " +
    "the draft's stages (the stages its panel binds, the stages those read " +
    "through inputs, and stages added to the draft since the editor " +
    "opened) with its declaration, run status, columns and the problems " +
    "the editor shows for it; the board stages those stages read; which " +
    "stages the chat may change; and the version client_update_panel_draft " +
    "takes.",
  input_schema: {
    type: "object",
    required: ["editor_opening_id"],
    properties: {
      editor_opening_id: EDITOR_OPENING_ID_PROPERTY,
    },
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Read panel draft",
  displayDescription:
    "Returns the unsaved panel open in the panel editor to the chat. Changes nothing.",
});
